package com.schoolconnect.app.feature.teachers

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppOutlinedTextField
import com.schoolconnect.app.designsystem.AppPrimary

@Composable
fun TeachersScreen(
    state: TeachersUiState,
    onEvent: (TeachersUiEvent) -> Unit,
    onBack: () -> Unit
) {
    val filteredItems = remember(state.items, state.searchQuery) {
        val query = state.searchQuery.trim()
        state.items.filter { query.isBlank() || it.contains(query, ignoreCase = true) }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(AppBackground)
            .padding(horizontal = 24.dp, vertical = 24.dp),
        verticalArrangement = Arrangement.spacedBy(24.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text("Home  /  Teachers", color = AppMuted, fontSize = 12.sp)
            Text("Manage Teachers", color = AppPageHeader, fontSize = 28.sp, fontWeight = FontWeight.SemiBold)
            Text("Create, update and manage teacher records and assignments.", color = AppPageSubtitle, fontSize = 14.sp)
        }

        Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.fillMaxWidth()) {
            TeacherSummaryCard("Total Teachers", state.items.size.toString(), Modifier.weight(1f))
            TeacherSummaryCard("Visible Results", filteredItems.size.toString(), Modifier.weight(1f))
            TeacherSummaryCard("Assignments", "—", Modifier.weight(1f))
        }

        AppCard(modifier = Modifier.fillMaxWidth()) {
            Column(modifier = Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                    AppOutlinedTextField(
                        value = state.searchQuery,
                        onValueChange = { onEvent(TeachersUiEvent.SearchChanged(it)) },
                        modifier = Modifier.weight(1f),
                        placeholder = "Search by teacher name or employee ID"
                    )
                    Spacer(Modifier.width(12.dp))
                    AppOutlinedButton(
                        onClick = { onEvent(TeachersUiEvent.RefreshClicked) },
                        border = BorderStroke(1.dp, AppBorder)
                    ) {
                        Icon(Icons.Default.Refresh, contentDescription = "Refresh teachers")
                        Spacer(Modifier.width(8.dp))
                        Text("Refresh")
                    }
                }

                TeacherTableHeader()
                Box(
                    modifier = Modifier.fillMaxWidth().height(260.dp),
                    contentAlignment = Alignment.Center
                ) {
                    when {
                        state.isLoading -> CircularProgressIndicator(color = AppPrimary)
                        state.errorMessage.isNotBlank() -> AppDialogErrorMessage(state.errorMessage)
                        filteredItems.isEmpty() -> Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            Text("No teachers found", color = AppNavy, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
                            Text("Try a different search or refresh the list.", color = AppMuted, fontSize = 14.sp)
                        }
                        else -> LazyColumn(modifier = Modifier.fillMaxSize(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                            items(filteredItems, key = { it }) { teacher -> TeacherRow(teacher) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TeacherSummaryCard(title: String, value: String, modifier: Modifier = Modifier) {
    AppCard(modifier = modifier) {
        Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(title, color = AppMuted, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
            Text(value, color = AppNavy, fontSize = 28.sp, fontWeight = FontWeight.SemiBold)
        }
    }
}

@Composable
private fun TeacherTableHeader() {
    Row(
        modifier = Modifier.fillMaxWidth().background(Color(0xFFF8FAFC)).border(1.dp, AppBorder, RoundedCornerShape(6.dp)).padding(12.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text("Teacher", Modifier.weight(1.6f), color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        Text("Employee ID", Modifier.weight(1f), color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        Text("Role", Modifier.weight(1f), color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        Text("Status", Modifier.weight(.8f), color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
    }
}

@Composable
private fun TeacherRow(value: String) {
    Row(
        modifier = Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(6.dp)).padding(12.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(value, Modifier.weight(1.6f), color = AppNavy, fontSize = 14.sp, maxLines = 1, overflow = TextOverflow.Ellipsis)
        Text("—", Modifier.weight(1f), color = AppMuted, fontSize = 14.sp)
        Text("Teacher", Modifier.weight(1f), color = AppMuted, fontSize = 14.sp)
        Text("Active", Modifier.weight(.8f), color = Color(0xFF16A34A), fontSize = 14.sp)
    }
}
